/* @jsx h */
import {
  defineComponent,
  getCurrentInstance,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type VNode,
} from "vue";

export type SectionedBodySection = {
  id: string;
  label: string;
  icon?: string;
};

export type SectionedBodyProps = {
  sections: SectionedBodySection[];
  ariaLabel?: string;
};

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;

  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }

  return null;
};

export const SectionedBody = defineComponent<SectionedBodyProps>({
  name: "SectionedBody",
  props: ["sections", "ariaLabel"],
  setup(props, { slots }) {
    const instanceId = getCurrentInstance()?.uid ?? 0;
    const rootElement = ref<HTMLElement | null>(null);
    const mobileNavigation = ref<HTMLElement | null>(null);
    const activeSectionId = ref(props.sections[0]?.id ?? "");
    const sectionElements = new Map<string, HTMLElement>();
    const navigationElements = new Map<string, HTMLButtonElement>();
    let scrollParent: HTMLElement | null = null;
    let animationFrame: number | null = null;

    const sectionDomId = (sectionId: string) =>
      `sectioned-body-${instanceId}-${sectionId}`;
    const mobileNavigationHeight = () =>
      mobileNavigation.value?.offsetHeight ?? 0;

    const centerMobileNavigationItem = async (sectionId: string) => {
      await nextTick();

      const navigationItem = navigationElements.get(sectionId);
      const navigation = mobileNavigation.value;
      if (!navigationItem || !navigation || navigation.offsetHeight === 0) {
        return;
      }

      const navigationRect = navigation.getBoundingClientRect();
      const itemRect = navigationItem.getBoundingClientRect();
      const left =
        navigation.scrollLeft +
        itemRect.left -
        navigationRect.left -
        (navigation.clientWidth - itemRect.width) / 2;

      navigation.scrollTo({ left, behavior: "smooth" });
    };

    const updateActiveSection = () => {
      animationFrame = null;
      if (props.sections.length === 0) return;

      const scrollTop = scrollParent
        ? scrollParent.getBoundingClientRect().top
        : 0;
      const activationLine = scrollTop + mobileNavigationHeight() + 24;
      let activeId = props.sections[0].id;

      for (const section of props.sections) {
        const element = sectionElements.get(section.id);
        if (element && element.getBoundingClientRect().top <= activationLine) {
          activeId = section.id;
        }
      }

      const atBottom = scrollParent
        ? scrollParent.scrollTop + scrollParent.clientHeight >=
          scrollParent.scrollHeight - 2
        : window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight - 2;

      if (atBottom) activeId = props.sections[props.sections.length - 1].id;

      if (activeSectionId.value !== activeId) {
        activeSectionId.value = activeId;
        void centerMobileNavigationItem(activeId);
      }
    };

    const scheduleActiveSectionUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(updateActiveSection);
    };

    const scrollToSection = (sectionId: string) => {
      const element = sectionElements.get(sectionId);
      if (!element) return;

      activeSectionId.value = sectionId;
      void centerMobileNavigationItem(sectionId);
      const offset = mobileNavigationHeight() + 12;

      if (scrollParent) {
        const target =
          scrollParent.scrollTop +
          element.getBoundingClientRect().top -
          scrollParent.getBoundingClientRect().top -
          offset;
        scrollParent.scrollTo({ top: target, behavior: "smooth" });
      } else {
        const target =
          window.scrollY + element.getBoundingClientRect().top - offset;
        window.scrollTo({ top: target, behavior: "smooth" });
      }
    };

    onMounted(async () => {
      await nextTick();
      if (!rootElement.value) return;

      scrollParent = getScrollParent(rootElement.value);
      (scrollParent ?? window).addEventListener(
        "scroll",
        scheduleActiveSectionUpdate,
        { passive: true },
      );
      window.addEventListener("resize", scheduleActiveSectionUpdate, {
        passive: true,
      });
      updateActiveSection();
    });

    onBeforeUnmount(() => {
      (scrollParent ?? window).removeEventListener(
        "scroll",
        scheduleActiveSectionUpdate,
      );
      window.removeEventListener("resize", scheduleActiveSectionUpdate);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    });

    const NavigationItems = ({ mobile = false }: { mobile?: boolean }) =>
      props.sections.map((section) => {
        const active = activeSectionId.value === section.id;

        return (
          <button
            key={section.id}
            ref={(element) => {
              if (mobile && element) {
                navigationElements.set(
                  section.id,
                  element as HTMLButtonElement,
                );
              }
            }}
            type="button"
            class={[
              "flex shrink-0 items-center gap-2 rounded-lg text-left text-sm transition-colors",
              mobile ? "px-3 py-2 whitespace-nowrap" : "w-full px-3 py-2.5",
              active
                ? "bg-primary/10 font-semibold text-accent"
                : "text-base-content/70 hover:bg-base-200 hover:text-base-content",
            ]}
            aria-current={active ? "location" : undefined}
            aria-controls={sectionDomId(section.id)}
            onClick={() => scrollToSection(section.id)}
          >
            {section.icon ? (
              <span
                class="material-symbols-outlined text-lg"
                aria-hidden="true"
              >
                {section.icon}
              </span>
            ) : null}
            <span>{section.label}</span>
          </button>
        );
      });

    return () => (
      <div ref={rootElement} class="min-w-0 px-4">
        <nav
          ref={mobileNavigation}
          class="sticky top-0 z-20 -mx-4 mb-4 flex max-w-[100vw] gap-1 overflow-x-auto border-b border-base-300 bg-base-100/95 px-2 py-2 shadow-sm backdrop-blur xl:px-0 lg:hidden"
          aria-label={props.ariaLabel ?? "Page sections"}
        >
          {NavigationItems({ mobile: true })}
        </nav>

        <div class="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_200px]">
          <main class="min-w-0 space-y-6">
            {props.sections.map((section) => (
              <section
                key={section.id}
                id={sectionDomId(section.id)}
                ref={(element) => {
                  if (element)
                    sectionElements.set(section.id, element as HTMLElement);
                }}
                class="scroll-mt-20 lg:scroll-mt-4"
                aria-label={section.label}
              >
                {slots[section.id]?.() as VNode[] | undefined}
              </section>
            ))}
          </main>

          <aside class="hidden lg:block">
            <nav
              class="sticky top-4 space-y-1"
              aria-label={props.ariaLabel ?? "Page sections"}
            >
              {NavigationItems({ mobile: false })}
            </nav>
          </aside>
        </div>
      </div>
    );
  },
});
